import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import type { ProcurementKpi } from './types'

function Delta({ value }: { value: number | null }) {
  if (value === null || value === 0) return null
  const up = value > 0
  const cls = up ? 'text-emerald-600' : 'text-red-600'
  return (
    <span className={`text-xs font-medium ${cls}`}>
      {up ? '↑' : '↓'} {Math.abs(value)}% vs прошлый период
    </span>
  )
}

interface CardProps {
  label: string
  value: ReactNode
  tone?: 'blue' | 'neutral' | 'red' | 'amber' | 'emerald'
  hint?: ReactNode
  to?: string
}

function Card({ label, value, tone = 'neutral', hint, to }: CardProps) {
  const bg = {
    blue: 'bg-blue-50/60 border-blue-100',
    neutral: 'bg-gray-50 border-gray-200',
    red: 'bg-red-50/60 border-red-100',
    amber: 'bg-amber-50/60 border-amber-100',
    emerald: 'bg-emerald-50/60 border-emerald-100',
  }[tone]

  const inner = (
    <div className={`rounded-2xl border ${bg} p-4 h-full flex flex-col justify-between`}>
      <div className="text-sm text-gray-600">{label}</div>
      <div className="text-3xl font-bold text-gray-900 leading-tight mt-2">{value}</div>
      {hint && <div className="mt-1">{hint}</div>}
    </div>
  )
  return to ? (
    <Link to={to} className="block hover:shadow-sm transition-shadow">
      {inner}
    </Link>
  ) : (
    inner
  )
}

export function ProcurementKpiCards({ kpi }: { kpi: ProcurementKpi }) {
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
      <Card
        label="Новых позиций"
        value={kpi.newPositions.count}
        tone="blue"
        hint={<span className="text-xs text-blue-700">без квот</span>}
        to="/procurement/queue?filter=mine-new"
      />
      <Card
        label="В работе"
        value={kpi.inProgress.count}
        tone="neutral"
        hint={<span className="text-xs text-gray-500">ждут ответ</span>}
        to="/procurement/queue?filter=mine-waiting"
      />
      <Card
        label="Горящих"
        value={kpi.burning.count}
        tone="red"
        hint={<span className="text-xs text-red-700">приоритет/дедлайн</span>}
        to="/procurement/queue?filter=burning"
      />
      <Card
        label={`Без движения > ${kpi.staleItems.daysThreshold}д`}
        value={kpi.staleItems.count}
        tone="amber"
        hint={<span className="text-xs text-amber-700">напомнить поставщикам</span>}
        to="/procurement/queue?filter=stale"
      />
      <Card
        label="Квот сегодня"
        value={kpi.quotesToday.count}
        tone="emerald"
        hint={
          <span className="text-xs text-emerald-700">ср. {kpi.quotesToday.dailyAverage}/день</span>
        }
      />
      <Card
        label="Моя маржа"
        value={kpi.myMargin.current === null ? '—' : `${kpi.myMargin.current}%`}
        tone="neutral"
        hint={<Delta value={kpi.myMargin.deltaPercent} />}
      />
    </div>
  )
}
